import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from '../../node_modules/classnames/index.js';
import { useSSRSafeId } from '@react-aria/ssr';
import styles from './Radio.module.scss.js';
import { applyCommonProps } from '../utility/commonProps.js';
import { invariant } from '../utility/invariant.js';

var _excluded = ["a11yLabelledBy", "ariaLabel", "checked", "className", "disabled", "id", "label", "name", "onChange", "radioProps", "value"],
  _excluded2 = ["className"];
/**
 * Radio Buttons represent a group of mutually exclusive choices.
 *
 * {@link https://livingdesign.walmart.com/components/radio/ Guidelines}
 * {@link https://livingdesign.walmart.com/components/radio/react/ React documentation}
 *
 * ### Content
 *
 * #### Label
 *
 * The Radio must include a label describing the option being selected. Provide a label through the `label` prop.
 *
 * Alternatively, a separate element can be used to label the Radio using `a11yLabelledBy` prop or aria-label can be provided using `ariaLabel` prop.
 *
 * In this case, an `a11yLabelledBy` that references the labelling component's `id` must be provided.
 *
 * (Note: `label`, `a11yLabelledBy` and `ariaLabel` props are mutually exclusive.)
 *
 * ```tsx
 * <Radio
 *   label="My radio"
 *   name="myRadioGroup"
 *   onChange={(event) => console.log(event)}
 * />
 * ```
 *
 * *OR*
 *
 * ```tsx
 * <Radio
 *   a11yLabelledBy="custom-label-id"
 *   name="myRadioGroup"
 *   onChange={(event) => console.log(event)}
 * >
 * <span id="custom-label-id">My radio</span>
 * ```
 *
 * *OR*
 *
 * ```tsx
 * <Radio
 *   ariaLabel="aria-label"
 *   name="myRadioGroup"
 *   onChange={(event) => console.log(event)}
 * >
 * ```
 *
 * #### Name
 *
 * The Radio requires a `name` property for keyboard navigation to work correctly with groups of Radio elements. Ensure that `name` is set to the same value for the group.
 *
 * ```tsx
 * <Radio
 *   label="One"
 *   name="numberRadio"
 *   onChange={(event) => console.log(event)}
 * />
 * <Radio
 *   label="Two"
 *   name="numberRadio"
 *   onChange={(event) => console.log(event)}
 * />
 * <Radio
 *   label="Three"
 *   name="numberRadio"
 *   onChange={(event) => console.log(event)}
 * />
 * ```
 *
 * ### Interaction
 *
 * The Radio can be configured as selected by using the `checked` property. Listen to changes in selection by passing an event handler to the `onChange` property.
 *
 * ```tsx
 * export default () => {
 *   const [value, setValue] = React.useState("0");
 *
 *   return (
 *     <Radio
 *       checked={value === "0"}
 *       label="No"
 *       onChange={(event) => setValue(event.target.value)}
 *       name="name"
 *       value="0"
 *     />
 *     <Radio
 *       checked={value === "1"}
 *       label="Yes"
 *       onChange={(event) => setValue(event.target.value)}
 *       name="name"
 *       value="1"
 *     />
 *   );
 * };
 * ```
 */
var Radio = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, Radio),
    a11yLabelledBy = _applyCommonProps.a11yLabelledBy,
    ariaLabel = _applyCommonProps.ariaLabel,
    _applyCommonProps$che = _applyCommonProps.checked,
    checked = _applyCommonProps$che === void 0 ? false : _applyCommonProps$che,
    className = _applyCommonProps.className,
    _applyCommonProps$dis = _applyCommonProps.disabled,
    disabled = _applyCommonProps$dis === void 0 ? false : _applyCommonProps$dis,
    initialId = _applyCommonProps.id,
    label = _applyCommonProps.label,
    name = _applyCommonProps.name,
    onChange = _applyCommonProps.onChange,
    _applyCommonProps$rad = _applyCommonProps.radioProps,
    radioProps = _applyCommonProps$rad === void 0 ? {} : _applyCommonProps$rad,
    value = _applyCommonProps.value,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  var labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) + (ariaLabel ? 1 : 0) === 1;
  invariant(labelCount, '`Radio` accessibility violation. `Radio` requires a `label` OR an `a11yLabelledBy` OR an `ariaLabel`.');
  var radioClassName = radioProps.className,
    radioRest = _objectWithoutProperties(radioProps, _excluded2);
  var id = useSSRSafeId(initialId);
  return /*#__PURE__*/React.createElement("label", _extends({
    className: classNames(styles.label, checked && styles.checked, disabled && styles.disabled, className),
    htmlFor: id
  }, rest), /*#__PURE__*/React.createElement("input", _extends({
    checked: checked,
    className: classNames(styles.input, radioClassName),
    disabled: disabled,
    id: id,
    name: name,
    onChange: onChange,
    ref: ref,
    type: "radio"
  }, a11yLabelledBy && {
    'aria-labelledby': a11yLabelledBy
  }, ariaLabel && {
    'aria-label': ariaLabel
  }, value && {
    value: value
  }, radioRest)), /*#__PURE__*/React.createElement("svg", {
    "aria-hidden": true,
    className: styles.radioInput,
    viewBox: "0 0 20 20",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("circle", {
    className: styles.radioInputOuter,
    cx: "10",
    cy: "10"
  }), /*#__PURE__*/React.createElement("circle", {
    className: styles.radioInputInner,
    cx: "10",
    cy: "10"
  })), label && /*#__PURE__*/React.createElement("span", {
    className: styles.labelText
  }, label));
});
Radio.displayName = 'Radio';

export { Radio };
